import { useMemo, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';

interface Option<T extends string> {
  value: T;
  label: string;
  description?: string;
}

const pad2 = (value: number) => String(value).padStart(2, '0');

function parseDate(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  const now = new Date();
  return {
    year: Number.isFinite(year) ? year : now.getFullYear(),
    month: Number.isFinite(month) ? month : now.getMonth() + 1,
    day: Number.isFinite(day) ? day : now.getDate(),
  };
}

function daysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate();
}

function formatDate(year: number, month: number, day: number) {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function formatDateLabel(value: string) {
  if (!value) return '请选择出生日期';
  const { year, month, day } = parseDate(value);
  return `${year} 年 ${month} 月 ${day} 日`;
}

function parseTime(value: string) {
  const [hour, minute] = value.split(':').map(Number);
  return {
    hour: Number.isFinite(hour) ? hour : 0,
    minute: Number.isFinite(minute) ? minute : 0,
  };
}

function formatTime(hour: number, minute: number) {
  return `${pad2(hour)}:${pad2(minute)}`;
}

function getShichen(hour: number) {
  if (hour === 23 || hour === 0) return '子时 23:00–00:59';
  if (hour <= 2) return '丑时 01:00–02:59';
  if (hour <= 4) return '寅时 03:00–04:59';
  if (hour <= 6) return '卯时 05:00–06:59';
  if (hour <= 8) return '辰时 07:00–08:59';
  if (hour <= 10) return '巳时 09:00–10:59';
  if (hour <= 12) return '午时 11:00–12:59';
  if (hour <= 14) return '未时 13:00–14:59';
  if (hour <= 16) return '申时 15:00–16:59';
  if (hour <= 18) return '酉时 17:00–18:59';
  if (hour <= 20) return '戌时 19:00–20:59';
  return '亥时 21:00–22:59';
}

function isBoundaryTime(hour: number, minute: number) {
  return minute <= 10 || minute >= 50 || hour === 23 || hour === 0;
}

function FieldShell({
  label,
  required,
  helper,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  helper?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="mb-1.5 text-sm text-fg-1">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </p>
      {children}
      {error ? (
        <p className="mt-1.5 text-xs text-danger">{error}</p>
      ) : helper ? (
        <p className="mt-1.5 text-xs text-fg-muted leading-relaxed">{helper}</p>
      ) : null}
    </div>
  );
}

function PickerButton({
  open,
  disabled,
  invalid,
  children,
  onClick,
}: {
  open: boolean;
  disabled?: boolean;
  invalid?: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'flex h-12 w-full items-center justify-between rounded-lg border px-4 text-left text-sm transition-all duration-fast',
        'bg-bg-1 text-fg shadow-sm disabled:cursor-not-allowed disabled:opacity-50',
        invalid
          ? 'border-danger'
          : open
            ? 'border-accent shadow-gold'
            : 'border-border hover:border-accent',
      )}
    >
      <span>{children}</span>
      <span className={clsx('text-accent transition-transform', open && 'rotate-180')}>⌄</span>
    </button>
  );
}

export function BirthDatePicker({
  value,
  onChange,
  label = '出生日期（公历）',
  required,
  minYear = 1900,
  maxYear = new Date().getFullYear(),
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  minYear?: number;
  maxYear?: number;
  error?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const selected = parseDate(value);
  const years = useMemo(
    () => Array.from({ length: maxYear - minYear + 1 }, (_, index) => maxYear - index),
    [maxYear, minYear],
  );
  const months = useMemo(() => Array.from({ length: 12 }, (_, index) => index + 1), []);
  const dayCount = daysInMonth(selected.year, selected.month);
  const days = useMemo(() => Array.from({ length: dayCount }, (_, index) => index + 1), [dayCount]);

  const setPart = (patch: Partial<typeof selected>) => {
    const next = { ...selected, ...patch };
    const safeDay = Math.min(next.day, daysInMonth(next.year, next.month));
    onChange(formatDate(next.year, next.month, safeDay));
  };

  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      helper="用于建立命盘基准；如接近节气或时辰边界，后续会建议定盘校准。"
    >
      <div className="relative">
        <PickerButton open={open} invalid={Boolean(error)} onClick={() => setOpen((v) => !v)}>
          {formatDateLabel(value)}
        </PickerButton>
        {open && (
          <div className="absolute left-1/2 z-30 mt-2 w-[min(100vw-2rem,100%)] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-bg-1 shadow-lg sm:left-0 sm:w-full sm:translate-x-0">
            <div className="border-b border-border px-4 py-3">
              <p className="font-serif text-base text-accent">校准出生日期</p>
              <p className="mt-1 text-xs text-fg-muted">三列校准，让日期录入更稳定、清晰</p>
            </div>
            <div className="grid grid-cols-3 gap-0 divide-x divide-border">
              <PickerColumn title="年">
                {years.map((year) => (
                  <PickerOption
                    key={year}
                    active={year === selected.year}
                    onClick={() => setPart({ year })}
                  >
                    {year} 年
                  </PickerOption>
                ))}
              </PickerColumn>
              <PickerColumn title="月">
                {months.map((month) => (
                  <PickerOption
                    key={month}
                    active={month === selected.month}
                    onClick={() => setPart({ month })}
                  >
                    {month} 月
                  </PickerOption>
                ))}
              </PickerColumn>
              <PickerColumn title="日">
                {days.map((day) => (
                  <PickerOption
                    key={day}
                    active={day === selected.day}
                    onClick={() => setPart({ day })}
                  >
                    {day} 日
                  </PickerOption>
                ))}
              </PickerColumn>
            </div>
            <div className="flex flex-col gap-3 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-xs text-fg-muted">已选：{formatDateLabel(value)}</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md bg-accent px-4 py-2 text-sm text-accent-fg"
              >
                确认
              </button>
            </div>
          </div>
        )}
      </div>
    </FieldShell>
  );
}

export function BirthTimePicker({
  value,
  onChange,
  label = '出生时间（24h）',
  required,
  disabled,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const selected = parseTime(value || '00:00');
  const hours = useMemo(() => Array.from({ length: 24 }, (_, index) => index), []);
  const minutes = useMemo(() => Array.from({ length: 60 }, (_, index) => index), []);
  const shichen = getShichen(selected.hour);

  const setPart = (patch: Partial<typeof selected>) => {
    const next = { ...selected, ...patch };
    onChange(formatTime(next.hour, next.minute));
  };

  return (
    <FieldShell
      label={label}
      required={required}
      error={error}
      helper={
        disabled
          ? '时辰未知时，先排年/月/日柱；后续可通过定盘校准补足判断。'
          : `${shichen}${isBoundaryTime(selected.hour, selected.minute) ? ' · 接近时辰边界，建议开启定盘校准' : ''}`
      }
    >
      <div className="relative">
        <PickerButton
          open={open}
          disabled={disabled}
          invalid={Boolean(error)}
          onClick={() => setOpen((v) => !v)}
        >
          {disabled ? '时辰未知' : value ? `${value} · ${shichen.split(' ')[0]}` : '请选择出生时间'}
        </PickerButton>
        {open && !disabled && (
          <div className="absolute left-1/2 z-30 mt-2 w-[min(100vw-2rem,100%)] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-bg-1 shadow-lg sm:left-0 sm:w-full sm:translate-x-0">
            <div className="border-b border-border px-4 py-3">
              <p className="font-serif text-base text-accent">校准出生时间</p>
              <p className="mt-1 text-xs text-fg-muted">精确到分钟，并显示对应十二时辰</p>
            </div>
            <div className="grid grid-cols-2 divide-x divide-border">
              <PickerColumn title="时">
                {hours.map((hour) => (
                  <PickerOption
                    key={hour}
                    active={hour === selected.hour}
                    onClick={() => setPart({ hour })}
                  >
                    {pad2(hour)} 时
                  </PickerOption>
                ))}
              </PickerColumn>
              <PickerColumn title="分">
                {minutes.map((minute) => (
                  <PickerOption
                    key={minute}
                    active={minute === selected.minute}
                    onClick={() => setPart({ minute })}
                  >
                    {pad2(minute)} 分
                  </PickerOption>
                ))}
              </PickerColumn>
            </div>
            <div className="flex flex-col gap-3 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-xs text-fg-muted">
                {formatTime(selected.hour, selected.minute)} · {shichen}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md bg-accent px-4 py-2 text-sm text-accent-fg"
              >
                确认
              </button>
            </div>
          </div>
        )}
      </div>
    </FieldShell>
  );
}

export function SelectField<T extends string>({
  value,
  options,
  onChange,
  label,
  required,
  placeholder = '请选择',
  error,
}: {
  value: T | '';
  options: Option<T>[];
  onChange: (value: T) => void;
  label: string;
  required?: boolean;
  placeholder?: string;
  error?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const current = options.find((option) => option.value === value);

  return (
    <FieldShell label={label} required={required} error={error}>
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={clsx(
            'flex min-h-12 w-full items-center justify-between rounded-lg border px-4 text-left text-sm transition-all duration-fast',
            'bg-bg-1 text-fg shadow-sm',
            open ? 'border-accent shadow-gold' : 'border-border hover:border-accent',
          )}
        >
          <span>
            <span className={current ? 'text-fg' : 'text-fg-muted'}>
              {current?.label ?? placeholder}
            </span>
            {current?.description && (
              <span className="mt-0.5 block text-xs text-fg-muted">{current.description}</span>
            )}
          </span>
          <span className={clsx('text-accent transition-transform', open && 'rotate-180')}>⌄</span>
        </button>
        {open && (
          <div className="absolute left-1/2 z-30 mt-2 w-[min(100vw-2rem,100%)] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-bg-1 p-1 shadow-lg sm:left-0 sm:w-full sm:translate-x-0">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                className={clsx(
                  'w-full rounded-lg px-3 py-2.5 text-left text-sm transition-colors duration-fast',
                  option.value === value
                    ? 'bg-accent-soft text-accent'
                    : 'text-fg-1 hover:bg-bg-2 hover:text-fg',
                )}
              >
                <span className="block">{option.label}</span>
                {option.description && (
                  <span className="mt-0.5 block text-xs text-fg-muted">{option.description}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </FieldShell>
  );
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  label,
  required,
}: {
  value: T;
  options: Option<T>[];
  onChange: (value: T) => void;
  label: string;
  required?: boolean;
}) {
  return (
    <FieldShell label={label} required={required}>
      <div className="grid gap-2 sm:grid-cols-3">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={clsx(
              'rounded-lg border px-3 py-3 text-left text-sm transition-all duration-fast',
              option.value === value
                ? 'border-accent bg-accent-soft text-accent shadow-gold'
                : 'border-border bg-bg-1 text-fg-1 hover:border-accent hover:text-fg',
            )}
          >
            <span className="block font-medium">{option.label}</span>
            {option.description && (
              <span className="mt-1 block text-xs text-fg-muted">{option.description}</span>
            )}
          </button>
        ))}
      </div>
    </FieldShell>
  );
}

function PickerColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="sticky top-0 z-10 border-b border-border bg-bg-1 px-3 py-2 text-xs text-fg-muted">
        {title}
      </p>
      <div className="max-h-56 overflow-y-auto p-1 sm:max-h-64">{children}</div>
    </div>
  );
}

function PickerOption({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'mb-1 w-full rounded-md px-3 py-2 text-left text-sm transition-colors duration-fast',
        active ? 'bg-accent-soft text-accent' : 'text-fg-1 hover:bg-bg-2 hover:text-fg',
      )}
    >
      {children}
    </button>
  );
}
